import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { EntityModal } from '../common';
import { useAutoAssignSeats } from '../../hooks/useFloorPlans';
import type { AutoAssignSeatsResult, AutoAssignSeatsRequest } from '../../api/floor-plans';

type Strategy = AutoAssignSeatsRequest['strategy'];

interface StrategyOption {
  value: Strategy;
  label: string;
  hint: string;
}

const STRATEGIES: StrategyOption[] = [
  {
    value: 'BY_GROUP',
    label: 'By family group',
    hint: 'Tables are tagged by family group; same-group guests stay together.',
  },
  {
    value: 'BY_SIDE',
    label: 'By side (bride / groom)',
    hint: 'Each side gets its own tables.',
  },
  {
    value: 'SEPARATE_MF',
    label: 'Separate male and female',
    hint: 'Tables are split into male-only and female-only. Mixed-demographic guests can sit anywhere.',
  },
  {
    value: 'KIDS_WITH_ADULTS',
    label: 'Kids with adults',
    hint: 'No demographic constraint — any guest at any table.',
  },
  {
    value: 'KIDS_SEPARATE',
    label: 'Kids on separate tables',
    hint: 'Kid-only and adult-only tables.',
  },
];

const TABLE_TYPES: NonNullable<
  NonNullable<AutoAssignSeatsRequest['autoAddTables']>['tableType']
>[] = ['ROUND', 'OVAL', 'SQUARE', 'RECTANGULAR'];

export interface AutoAssignSeatsModalProps {
  open: boolean;
  onClose: () => void;
  floorPlanId: string;
}

export function AutoAssignSeatsModal({ open, onClose, floorPlanId }: AutoAssignSeatsModalProps) {
  const [strategy, setStrategy] = useState<Strategy>('BY_GROUP');
  const [respectKeepTogether, setRespectKeepTogether] = useState(true);
  const [autoAddEnabled, setAutoAddEnabled] = useState(false);
  const [seatsPerTable, setSeatsPerTable] = useState(10);
  const [tableType, setTableType] =
    useState<NonNullable<NonNullable<AutoAssignSeatsRequest['autoAddTables']>['tableType']>>(
      'ROUND',
    );

  const [preview, setPreview] = useState<AutoAssignSeatsResult | null>(null);

  const mutation = useAutoAssignSeats(floorPlanId);

  function buildBody(dryRun: boolean): AutoAssignSeatsRequest {
    const body: AutoAssignSeatsRequest = { strategy, respectKeepTogether, dryRun };
    if (autoAddEnabled) body.autoAddTables = { seatsPerTable, tableType };
    return body;
  }

  async function runPreview() {
    setPreview(null);
    try {
      const result = await mutation.mutateAsync(buildBody(true));
      setPreview(result);
    } catch {
      // Error is rendered via `mutation.error`; we just need to swallow the
      // rejection so it doesn't bubble up as an unhandled promise rejection.
    }
  }

  async function runApply() {
    try {
      const result = await mutation.mutateAsync(buildBody(false));
      setPreview(result);
      // Close shortly after a successful apply so the user can see the toast/result.
      if (result.applied && result.unassigned.length === 0) {
        setTimeout(() => onClose(), 600);
      }
    } catch {
      /* see runPreview */
    }
  }

  const hasShortfall = !!preview && preview.shortfall.partySeats > 0;
  const willAutoAddTables = autoAddEnabled && hasShortfall;

  return (
    <EntityModal open={open} onClose={onClose} title="Auto-Assign Seats" size="lg">
      <div className="space-y-5">
        <section>
          <h3 className="text-sm font-medium text-gray-900 mb-2">Strategy</h3>
          <div className="space-y-2" role="radiogroup" aria-label="Seating strategy">
            {STRATEGIES.map((opt) => (
              <label
                key={opt.value}
                className={`flex items-start gap-2 p-2 rounded-md border cursor-pointer ${
                  strategy === opt.value
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="seating-strategy"
                  className="mt-1"
                  checked={strategy === opt.value}
                  onChange={() => setStrategy(opt.value)}
                  data-testid={`strategy-${opt.value}`}
                />
                <span>
                  <span className="block text-sm font-medium text-gray-900">{opt.label}</span>
                  <span className="block text-xs text-gray-500">{opt.hint}</span>
                </span>
              </label>
            ))}
          </div>
        </section>

        <section>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={respectKeepTogether}
              onChange={(e) => setRespectKeepTogether(e.target.checked)}
              data-testid="respect-keep-together"
            />
            <span className="text-sm text-gray-900">
              Use "keep together" as default for guests with no explicit preference
            </span>
          </label>
        </section>

        <section className="border-t border-gray-200 pt-4">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={autoAddEnabled}
              onChange={(e) => setAutoAddEnabled(e.target.checked)}
              data-testid="auto-add-toggle"
            />
            <span className="text-sm text-gray-900">
              If there aren't enough seats, add new tables to absorb the overflow
            </span>
          </label>
          {autoAddEnabled && (
            <div className="mt-2 grid grid-cols-2 gap-3 pl-6">
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-700">Seats per table</span>
                <input
                  type="number"
                  min={2}
                  max={50}
                  value={seatsPerTable}
                  onChange={(e) => setSeatsPerTable(parseInt(e.target.value, 10) || 0)}
                  className="px-2 py-1 border border-gray-300 rounded text-sm"
                  data-testid="auto-add-seats"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-700">Table shape</span>
                <select
                  value={tableType}
                  onChange={(e) => setTableType(e.target.value as typeof tableType)}
                  className="px-2 py-1 border border-gray-300 rounded text-sm"
                  data-testid="auto-add-shape"
                >
                  {TABLE_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t.charAt(0) + t.slice(1).toLowerCase()}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
        </section>

        {preview && (
          <section
            className="border border-gray-200 rounded-md p-3 bg-gray-50"
            data-testid="preview-result"
          >
            <div className="text-sm text-gray-900 font-medium mb-2">
              {preview.applied ? 'Applied' : 'Preview'}
            </div>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>
                Will seat: <strong>{preview.assignments.length}</strong> guest record(s)
              </li>
              {preview.unassigned.length > 0 && (
                <li className="text-amber-700">
                  Unseated: <strong>{preview.unassigned.length}</strong>
                </li>
              )}
              {preview.shortfall.partySeats > 0 && (
                <li className="text-amber-700">
                  Short by <strong>{preview.shortfall.partySeats}</strong> seat(s)
                </li>
              )}
              {preview.tablesAdded.length > 0 && (
                <li className="text-green-700">
                  {preview.applied ? 'Added' : 'Will add'}{' '}
                  <strong>{preview.tablesAdded.length}</strong> new table(s)
                </li>
              )}
            </ul>
            {willAutoAddTables && !preview.applied && (
              <p className="text-xs text-gray-500 mt-2">
                Apply will create {preview.tablesAdded.length} new table(s) and seat everyone.
              </p>
            )}
          </section>
        )}

        {mutation.error && (
          <p className="text-sm text-red-600" data-testid="auto-assign-error">
            {mutation.error.message}
          </p>
        )}
      </div>

      <div className="mt-6 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={runPreview}
          disabled={mutation.isPending}
          className="px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50 disabled:opacity-50"
          data-testid="auto-assign-preview"
        >
          {mutation.isPending && preview === null ? 'Calculating…' : 'Preview'}
        </button>
        <button
          type="button"
          onClick={runApply}
          disabled={mutation.isPending}
          className="px-3 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50 inline-flex items-center gap-1"
          data-testid="auto-assign-apply"
        >
          <Sparkles size={14} />
          {mutation.isPending && preview !== null ? 'Applying…' : 'Apply'}
        </button>
      </div>
    </EntityModal>
  );
}
